import { Injectable, inject } from "@angular/core";
import { Resolve, ActivatedRouteSnapshot, Router } from "@angular/router";
import {
  Observable,
  catchError,
  forkJoin,
  from,
  of,
  switchMap,
} from "rxjs";
import { BaseService } from "../base-service";
import { environment } from "../../../../environments/environment";
import { LogoutService } from "../logout-service";
import { CacheService } from "../cache-service";
import { ReusableService } from "../reusable-services";
import { Language } from "../../models/language";
import {
  mockLoginPageJSONResponse,
  mockLandingPageJSONResponse,
  mockCartPageLabels,
  mockSubscriptionsResponse,
  mockgetUserDetails,
  mockUserAccountPageJson,
  mockMyOrdersLabels,
  mockImportToCreateOrderLabels,
  mockSavedShoppingCartPageJson,
  mockErrorpagePageJson,
  mockLocalLabourMgmtPageJson,
  mockWspHomePageJSONResponse,
  mockWspMyFavoritesPageJSONResponse,
  mockWspFavoritesListProductsPageJSONResponse,
  mockWspProductDetailsPageJSONResponse,
  mockWspProductListPageJSONResponse,
  mockStatusMessagePollingResponse,
} from "../../utils/api-mocks";
import { API_PATHS } from "../../constants/api-paths";
import { CACHE_KEYS } from "../../constants/cache-keys";
import { SRP_ERRORS } from "../../constants/srp-errors";
import { SRP_ROUTES } from "../../constants/routes";
import { SrpContext } from "../../../header/user-profile/user-profile.model";
@Injectable({ providedIn: "root" })
export class PageResolver implements Resolve<any> {
  private readonly baseService = inject(BaseService);
  private readonly reusableService = inject(ReusableService);
  private readonly router = inject(Router);
  private readonly logoutService = inject(LogoutService);
  private readonly cacheService = inject(CacheService);

  userDetails = {
    sessionId: "",
    userName: "",
    userId: "",
    userEmail: "",
    userType: "",
    region: "",
    market: "",
    isAdmin: false,
    currentContext: {
      businessUnit: "",
      roleName: "-",
      dealerCode: "",
      dealerName: "",
      languageCode: "",
    },
    userFirstName: "",
    userLastName: "",
  };

  constructor() { }

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    const path = route.routeConfig.path;

    if (!environment.STUBS) {
      return this.resolveStubRoute(path);
    } else {

      // First get languages, then use the language to build the URL and fetch pageJSON
      return this.reusableService.getSupportedLanguages().pipe(
        switchMap((languages) => {
          // Choose the language you want to use (e.g., first in the list)
          return this.callPageJSON(languages, path);
        }),

        catchError((err) => {
          console.error('[Resolver] getUserRoles failed:', err);
          // ✅ allow navigation to continue with a fallback
          return of(null);
        })

      );
    }
  }

  private resolveStubRoute(path: string): Observable<any> {
    const mockMap: { test: (p: string) => boolean; mock: () => Observable<any> }[] = [
      { test: (p) => p.endsWith("login"), mock: () => mockLoginPageJSONResponse() },
      { test: (p) => p.endsWith("landingpage") || p.endsWith("landingPage"), mock: () => mockLandingPageJSONResponse() },
      { test: (p) => p.endsWith("local-settings"), mock: () => mockLocalLabourMgmtPageJson() },
      { test: (p) => p.endsWith("savedshoppingcart"), mock: () => mockSavedShoppingCartPageJson() },
      { test: (p) => p.includes("cart"), mock: () => mockCartPageLabels() },
      { test: (p) => p.endsWith("settings") || p.endsWith("Settings"), mock: () => mockgetUserDetails() },
      { test: (p) => p.endsWith("my-account"), mock: () => mockUserAccountPageJson() },
      { test: (p) => p.endsWith("error-page"), mock: () => mockErrorpagePageJson() },
      { test: (p) => p.endsWith("my-orders"), mock: () => mockMyOrdersLabels() },
      { test: (p) => p.endsWith(SRP_ROUTES.MYRETURNORDERS.PATH), mock: () => mockMyOrdersLabels() },
      { test: (p) => p.endsWith("import-to-create-order"), mock: () => mockImportToCreateOrderLabels() },
      { test: (p) => p.endsWith("workshop-products"), mock: () => mockWspHomePageJSONResponse() },
      { test: (p) => p.endsWith("my-favorites"), mock: () => mockWspMyFavoritesPageJSONResponse() },
      { test: (p) => p.endsWith("product") || p.endsWith("frequently-purchased"), mock: () => mockWspFavoritesListProductsPageJSONResponse() },
      { test: (p) => p.endsWith("product-list"), mock: () => mockWspProductListPageJSONResponse() },
      { test: (p) => p.endsWith("product-details"), mock: () => mockWspProductDetailsPageJSONResponse() },
      { test: (p) => p.endsWith("service-messages-center"), mock: () => mockStatusMessagePollingResponse() },
      // { test: (p) => p.endsWith("status-message-details"), mock: () => mockStatusMessageDetailPage() },
    ];
    const match = mockMap.find((entry) => entry.test(path));
    const pageJSON$ = match ? match.mock() : mockSubscriptionsResponse();
    return forkJoin({
      languages: this.reusableService.getSupportedLanguages(),
      pageJSON: pageJSON$,
    });
  }

  private callPageJSON(languages: Language[], path: string) {
    const selectedLang = this.reusableService.getApplicableLanguage();
    const { request, url } = this.getPageJSONRequest(path, selectedLang);

    return from(this.baseService.post(url, request)).pipe(
      switchMap((pageJSON: any) => {
        // Treat missing/invalid page payload as a resolver failure and run the same recovery flow.
        if (!pageJSON?.data) {
          return this.handlePageJsonFailure(
            new Error("pageJSON.data is null or undefined"),
            path,
            request,
            languages
          );
        }

        return of({ languages, pageJSON });
      }),
      catchError((err) => this.handlePageJsonFailure(err, path, request, languages))
    );
  }

  private handlePageJsonFailure(err: any, path: string, request: any, languages: Language[]) {
    console.error(`[Resolver] callPageJSON failed for path: ${path}`, err);
    this.cacheService.addToCache(CACHE_KEYS.LOGIN.LOGIN_ERROR, SRP_ERRORS.LOGIN_ERRORS.PAGEJSON_FAILED);

    if (path?.toLowerCase() == SRP_ROUTES.LOGIN.PATH) { //Added to handle Login Page JSON Failure to avoid infinite loop
      console.log(`Error while loading content for Login page ${JSON.stringify(request)}`, err);
      alert('Unable to load login page content. Please try again later.');
      return of(null);
    }

    if (path?.toLowerCase() != SRP_ROUTES.LANDINGPAGE.PATH) {
      this.router.navigate([`/${SRP_ROUTES.LANDINGPAGE.PATH}`], { queryParams: { redirect: 'content' } });
      return of(null);
    }

    // Attempt logout, then redirect to login
    return from(this.logoutService.logout()).pipe(
      catchError((logoutErr) => {
        console.error('[Resolver] Logout failed:', logoutErr);
        this.cacheService.addToCache(CACHE_KEYS.LOGIN.FORCE_LOGIN, true);
        this.router.navigate([`/${SRP_ROUTES.LOGIN.PATH}`]);
        return of(null);
      }),
      switchMap(() => {
        // After successful logout, return fallback data
        this.router.navigate([`/${SRP_ROUTES.LOGIN.PATH}`]);

        return of({ languages, pageJSON: null });
      })
    );
  }

  private getPageJSONRequest(path: string, selectedLanguage: Language) {
    const srpContext = this.reusableService.getSrpContextVariable();
    if (srpContext) {
      this.populateUserDetails(srpContext);
    }

    this.userDetails.currentContext.languageCode = selectedLanguage.languageCode;
    const pathConfig = this.getPathConfiguration(path, selectedLanguage);
    return pathConfig;
  }

  private populateUserDetails(srpContext: SrpContext) {
    const dealerLists = srpContext?.userInfo?.dealerLists[0];
    this.userDetails.userName = srpContext.userInfo?.user.userName || "";
    this.userDetails.isAdmin = srpContext.userInfo?.user.isAdmin || false;
    this.userDetails.currentContext.roleName = dealerLists.roleList[0]?.roleName || "";
    this.userDetails.region = srpContext.userInfo?.user?.region?.toLowerCase() || "";
    this.userDetails.market = srpContext.userInfo?.user?.market?.toLowerCase() || "";
  }

  private getPathConfiguration(path: string, selectedLanguage: Language) {
    const pathMap: { [key: string]: { pagePath: string, url: string } } = {
      'login': {
        pagePath: `/content/srp/global/${selectedLanguage.languageCode}/login`,
        url: API_PATHS.PAGE_DATA.LOGIN_PAGE
      },
      'landingpage': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/landingPage`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'my-orders': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/orders`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      [SRP_ROUTES.MYRETURNORDERS.PATH]: {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/orders`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'error-page': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/page-not-found`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'import-to-create-order': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/import-to-create-order`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'order-details': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/order-details`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'local-settings': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/local-settings`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'order-preparation': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/order-preparation`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'my-account': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/user-account`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'order-details-preview': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/order-details-preview`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'sso-error-page': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/sso-error-page`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'savedshoppingcart': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/saved-shopping-cart`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'settings': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/user-settings`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'cart': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/cart`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'subscriptions': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/subscriptions`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'estimates': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/estimate`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'estimate-list': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/estimate-list`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'estimate-print': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/print-estimate`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
       'service-messages-center': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/service-messages-center`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'workshop-products': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/product-landing-page`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'my-favorites': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/my-favourites-list`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'product': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/frequently-purchased`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'frequently-purchased': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/frequently-purchased`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'product-list': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/product-listing-page`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
      'product-details': {
        pagePath: `/content/srp/${this.userDetails.region}/${this.userDetails.market}/${selectedLanguage.languageCode}/product-details-page`,
        url: API_PATHS.PAGE_DATA.GENERIC_PAGE
      },
    };

    const normalizedPath = path.toLowerCase().split('/').pop() || '';
    const config = pathMap[normalizedPath];

    if (config) {
      return {
        request: {
          pagePath: config.pagePath,
          userDetails: this.userDetails
        },
        url: config.url
      };
    }

    return { request: undefined, url: undefined };
  }
}
